import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  CloudDownload,
  ExternalLink,
  Loader2,
  Timer,
  User2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { TestTypeBadge } from "@/components/ci/test-type-badge";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/contexts/auth-context";
import { useTimezone } from "@/contexts/timezone-context";
import { useCiRuns, useCiRunTriggerUsers } from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import type { CiRun } from "@/lib/types";
import { cn, formatDurationFromRange, formatRelativeTime } from "@/lib/utils";

const statusFilters = [
  { label: "All", value: "all" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Timed Out", value: "timed_out" },
  { label: "In Progress", value: "in_progress" },
] as const;

const triggerFilters = [
  { label: "All triggers", value: "all" },
  { label: "Scheduled (CI)", value: "ci" },
  { label: "Manual", value: "manual" },
] as const;

import { TEST_TYPE_FILTERS as testTypeFilters } from "@/lib/ci-filters";

const timeFilters = [
  { label: "All time", value: "all" },
  { label: "Last 24h", value: "24h" },
  { label: "Last 7d", value: "7d" },
  { label: "Last 30d", value: "30d" },
  { label: "Custom range", value: "custom" },
] as const;

type SortField = "started_at" | "duration" | "tests" | "status";
type SortDirection = "asc" | "desc";

// Single knob — bump if pages feel too short. Backend cap is 200
// (criu_ci.list_runs ``Query(le=200)``); raising this past that needs both.
const PAGE_SIZE = 50;

// Relative time-window widths in hours, keyed by URL value. When the user
// picks one of these we snapshot `now - hours` into the URL so the cutoff
// stays stable across page navigation and reloads.
const RELATIVE_WINDOW_HOURS: Record<string, number> = {
  "24h": 24,
  "7d": 168,
  "30d": 720,
};

function runDisplayStatus(run: CiRun): string {
  if (run.status === "completed" && run.failed > 0) return "failed";
  return run.status;
}

function isScheduled(triggeredBy: string | null): boolean {
  // github-actions[bot]: scheduled runs recorded before the move off GitHub.
  return triggeredBy === "ci" || triggeredBy === "github-actions[bot]";
}

function runDurationMs(run: CiRun): number {
  if (!run.started_at) return 0;
  const start = new Date(run.started_at).getTime();
  const end = run.completed_at ? new Date(run.completed_at).getTime() : Date.now();
  return end - start;
}

function statusSortOrder(run: CiRun): number {
  if (run.status === "in_progress") return 0;
  if (run.failed > 0 || run.status === "failed") return 1;
  if (run.status === "timed_out") return 2;
  return 3;
}

function TriggerBadge({ triggeredBy }: { triggeredBy: string | null }) {
  if (!triggeredBy) return <span className="text-muted-foreground">-</span>;
  const scheduled = isScheduled(triggeredBy);
  return (
    <Tooltip>
      <TooltipTrigger>
        <span
          className={cn(
            "inline-flex items-center gap-1 text-xs",
            scheduled ? "text-muted-foreground" : "text-foreground",
          )}
        >
          {scheduled ? <CalendarClock className="size-3" /> : <User2 className="size-3" />}
          {triggeredBy}
        </span>
      </TooltipTrigger>
      <TooltipContent>{scheduled ? "Scheduled run" : `Triggered by ${triggeredBy}`}</TooltipContent>
    </Tooltip>
  );
}

function SortableHeader({
  label,
  field,
  currentSort,
  currentDirection,
  onSort,
  icon,
}: {
  label: string;
  field: SortField;
  currentSort: SortField;
  currentDirection: SortDirection;
  onSort: (field: SortField) => void;
  icon?: React.ReactNode;
}) {
  const isActive = currentSort === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 text-left hover:text-foreground transition-colors"
    >
      {icon}
      {label}
      {isActive ? (
        currentDirection === "asc" ? (
          <ArrowUp className="size-3" />
        ) : (
          <ArrowDown className="size-3" />
        )
      ) : (
        <ArrowUpDown className="size-3 opacity-30" />
      )}
    </button>
  );
}

export function CiRunsPage() {
  usePageTitle("CI Runs");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatTimestamp } = useTimezone();
  const { user } = useAuth();

  const queryClient = useQueryClient();
  const [gcsSyncing, setGcsSyncing] = useState(false);

  // In local mode, reseed from local progress.json files on page load
  useEffect(() => {
    if (user?.local_mode) {
      fetch("/api/local/reseed", { method: "POST", credentials: "include" }).catch(() => {});
    }
  }, [user?.local_mode]);

  const syncFromGcs = useCallback(async () => {
    setGcsSyncing(true);
    try {
      await fetch("/api/local/sync-gcs", { method: "POST", credentials: "include" });
      await queryClient.invalidateQueries({ queryKey: ["ci-runs"] });
    } finally {
      setGcsSyncing(false);
    }
  }, [queryClient]);

  // Read filters from URL
  const statusFilter = searchParams.get("status") ?? "all";
  const triggerFilter = searchParams.get("trigger") ?? "all";
  const timeFilter = searchParams.get("time") ?? "all";
  const typeFilter = searchParams.get("type") ?? "all";
  const triggeredByFilter = searchParams.get("triggered_by") ?? "";
  const customFrom = searchParams.get("from") ?? "";
  const customTo = searchParams.get("to") ?? "";
  // For relative windows we snapshot the cutoff into `time_from` so it
  // doesn't drift forward as the user navigates between pages.
  const relativeTimeFrom = searchParams.get("time_from") ?? "";
  const sortField = (searchParams.get("sort") as SortField) ?? "started_at";
  const sortDirection = (searchParams.get("dir") as SortDirection) ?? "desc";
  const pageParam = Number.parseInt(searchParams.get("page") ?? "1", 10);
  const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const currentUsername = user?.username;

  // Filter changes reset to page 1 so the user doesn't sit on an out-of-range
  // page when the new total shrinks. The default (`isFilter = true`) covers
  // every non-pagination URL mutation that touches an api param.
  const setParam = useCallback(
    (key: string, value: string, isFilter = true) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (value === "all" || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
        if (isFilter) next.delete("page");
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSort = useCallback(
    (field: SortField) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (sortField === field) {
          next.set("dir", sortDirection === "asc" ? "desc" : "asc");
        } else {
          next.set("sort", field);
          next.set("dir", "desc");
        }
        return next;
      });
    },
    [setSearchParams, sortField, sortDirection],
  );

  const goToPage = useCallback(
    (page: number) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        if (page <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(page));
        }
        return next;
      });
    },
    [setSearchParams],
  );
  const { data: triggerUsers } = useCiRunTriggerUsers();

  // "My runs" target: the actual stored triggered_by value (CI_TRIGGERED_BY / INFERIZE_USER)
  // matching the Fleet username case-insensitively. Hidden when no run carries a matching value.
  const myTriggerName = useMemo(() => {
    if (!currentUsername) return undefined;
    return (triggerUsers ?? []).find(
      (triggerUser) => triggerUser.toLowerCase() === currentUsername.toLowerCase(),
    );
  }, [triggerUsers, currentUsername]);
  const myRunsActive =
    !!myTriggerName && triggeredByFilter.toLowerCase() === myTriggerName.toLowerCase();

  // Backfill the relative-window cutoff for URLs that arrived without one
  // (e.g. an old bookmark or a hand-written `?time=24h` link). Once written,
  // the cutoff is frozen until the user re-picks the filter.
  useEffect(() => {
    if (timeFilter === "all" || timeFilter === "custom") return;
    if (relativeTimeFrom) return;
    const hours = RELATIVE_WINDOW_HOURS[timeFilter];
    if (!hours) return;
    const cutoff = new Date(Date.now() - hours * 3600000).toISOString();
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("time_from", cutoff);
        return next;
      },
      { replace: true },
    );
  }, [timeFilter, relativeTimeFrom, setSearchParams]);

  // Build server-side query params. All filters are now server-side so
  // pagination composes correctly with them (one page == one slice of the
  // filtered total, no client-side trimming after the fetch).
  const apiParams = useMemo(() => {
    const params: Record<string, string> = {
      limit: String(PAGE_SIZE),
      offset: String((currentPage - 1) * PAGE_SIZE),
    };
    if (statusFilter !== "all") params.status = statusFilter;
    if (typeFilter !== "all") params.test_type = typeFilter;
    if (triggerFilter !== "all") params.trigger = triggerFilter;
    if (triggeredByFilter) params.triggered_by = triggeredByFilter;
    if (timeFilter === "custom") {
      if (customFrom) params.time_from = new Date(`${customFrom}T00:00:00Z`).toISOString();
      if (customTo) params.time_to = new Date(`${customTo}T23:59:59Z`).toISOString();
    } else if (timeFilter !== "all" && relativeTimeFrom) {
      // Snapshot from URL — set once at filter-selection time so the cutoff
      // doesn't creep forward as the user pages, which could otherwise skip
      // tail rows on the last page. The backfill effect above seeds it for
      // links that arrived without a snapshot.
      params.time_from = relativeTimeFrom;
    }
    return params;
  }, [
    statusFilter,
    typeFilter,
    triggerFilter,
    triggeredByFilter,
    timeFilter,
    customFrom,
    customTo,
    relativeTimeFrom,
    currentPage,
  ]);

  const { data: page, isLoading, isFetching } = useCiRuns(apiParams);
  const runs = page?.items;
  const totalRuns = page?.total ?? 0;
  const totalPages = totalRuns === 0 ? 0 : Math.ceil(totalRuns / PAGE_SIZE);

  // Sort is page-scoped: the server returns one slice ordered by
  // (started_at DESC, id DESC); the user's chosen sort reorders that slice in
  // place. It would be misleading to call this a global sort, but it's still
  // useful within a 50-row page.
  const sortedRuns = useMemo(() => {
    if (!runs) return [];
    const result = [...runs];
    result.sort((first, second) => {
      let comparison = 0;
      switch (sortField) {
        case "started_at":
          comparison =
            new Date(first.started_at ?? 0).getTime() - new Date(second.started_at ?? 0).getTime();
          break;
        case "duration":
          comparison = runDurationMs(first) - runDurationMs(second);
          break;
        case "tests":
          comparison = first.total_tests - second.total_tests;
          break;
        case "status":
          comparison = statusSortOrder(first) - statusSortOrder(second);
          break;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });
    return result;
  }, [runs, sortField, sortDirection]);

  // If the URL points past the last page (e.g. filters narrowed the result
  // set since the link was bookmarked), bounce back to the last valid page.
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      goToPage(totalPages);
    }
  }, [currentPage, totalPages, goToPage]);

  const inProgressCount = sortedRuns.filter((run) => run.status === "in_progress").length;
  const failedCount = sortedRuns.filter((run) => run.failed > 0 || run.status === "failed").length;

  return (
    <div className="mx-auto max-w-screen-xl space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">CI Runs</h1>
          <p className="text-sm text-muted-foreground">
            CRIU checkpoint/restore and Cryod K8s E2E integration test results
          </p>
        </div>
        {user?.local_mode && (
          <Button variant="outline" size="sm" onClick={syncFromGcs} disabled={gcsSyncing}>
            {gcsSyncing ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <CloudDownload className="size-4" />
            )}
            {gcsSyncing ? "Syncing..." : "Sync from GCS"}
          </Button>
        )}
      </div>

      {/* Stats bar */}
      {runs && (
        <div className="flex items-center gap-4 text-sm">
          <span className="font-medium">
            {totalRuns} {totalRuns === 1 ? "run" : "runs"}
          </span>
          {totalPages > 1 && (
            <span className="text-muted-foreground">
              Page {currentPage} of {totalPages}
            </span>
          )}
          {inProgressCount > 0 && (
            <span className="text-amber-500">
              {inProgressCount} in progress{totalPages > 1 ? " on this page" : ""}
            </span>
          )}
          {failedCount > 0 && (
            <span className="text-rose-500">
              {failedCount} with failures{totalPages > 1 ? " on this page" : ""}
            </span>
          )}
          {isFetching && !isLoading && (
            <Loader2
              className="size-3.5 animate-spin text-muted-foreground"
              aria-label="Refreshing"
            />
          )}
        </div>
      )}

      {/* Filters — single row, all aligned */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status pills */}
        <div className="flex gap-0.5 rounded-lg border p-0.5">
          {statusFilters.map(({ label, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setParam("status", value)}
              className={cn(
                "rounded-md px-3 py-1 text-sm font-medium transition-colors",
                statusFilter === value
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="h-5 w-px bg-border" />

        {/* Trigger */}
        <Select
          value={triggerFilter}
          onValueChange={(value) => value && setParam("trigger", value)}
        >
          <SelectTrigger className="w-40" size="sm">
            <SelectValue>
              {triggerFilters.find((filter) => filter.value === triggerFilter)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {triggerFilters.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* My runs quick filter — hidden when no stored triggered_by value matches the Fleet user */}
        {myTriggerName && (
          <button
            type="button"
            onClick={() => setParam("triggered_by", myRunsActive ? "" : myTriggerName)}
            className={cn(
              "rounded-md border px-3 py-1 text-sm font-medium transition-colors",
              myRunsActive
                ? "bg-primary text-primary-foreground border-primary shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
            aria-pressed={myRunsActive}
          >
            My runs
          </button>
        )}

        {/* Triggered by user picker */}
        <Select
          value={triggeredByFilter || "all"}
          onValueChange={(value) => {
            if (!value) return;
            setParam("triggered_by", value === "all" ? "" : value);
          }}
        >
          <SelectTrigger className="w-48" size="sm">
            <SelectValue>{triggeredByFilter || "Triggered by: anyone"}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Triggered by: anyone</SelectItem>
            {(triggerUsers ?? []).map((triggerUser) => (
              <SelectItem key={triggerUser} value={triggerUser}>
                {triggerUser}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Test type */}
        <Select value={typeFilter} onValueChange={(value) => value && setParam("type", value)}>
          <SelectTrigger className="w-36" size="sm">
            <SelectValue>
              {testTypeFilters.find((filter) => filter.value === typeFilter)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {testTypeFilters.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Time */}
        <Select
          value={timeFilter}
          onValueChange={(value) => {
            if (!value) return;
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set("time", value);
              const relativeHours = RELATIVE_WINDOW_HOURS[value];
              if (relativeHours) {
                // Snapshot the cutoff once so paging doesn't drift the
                // lower bound (the `time_from` URL key is consumed verbatim
                // by the backend).
                next.set("time_from", new Date(Date.now() - relativeHours * 3600000).toISOString());
                next.delete("from");
                next.delete("to");
              } else if (value === "custom") {
                next.delete("time_from");
                if (!next.get("from")) {
                  const twoWeeksAgo = new Date(Date.now() - 14 * 86400000);
                  next.set("from", twoWeeksAgo.toISOString().slice(0, 10));
                  next.set("to", new Date().toISOString().slice(0, 10));
                }
              } else {
                next.delete("time_from");
                next.delete("from");
                next.delete("to");
              }
              next.delete("page");
              return next;
            });
          }}
        >
          <SelectTrigger className="w-36" size="sm">
            <SelectValue>
              {timeFilters.find((filter) => filter.value === timeFilter)?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {timeFilters.map(({ label, value }) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Custom date range — inline */}
        {timeFilter === "custom" && (
          <>
            <div className="h-5 w-px bg-border" />
            <DatePicker
              value={customFrom ? new Date(`${customFrom}T00:00:00Z`) : undefined}
              onChange={(date) => setParam("from", date ? date.toISOString().slice(0, 10) : "")}
              placeholder="From"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <DatePicker
              value={customTo ? new Date(`${customTo}T00:00:00Z`) : undefined}
              onChange={(date) => setParam("to", date ? date.toISOString().slice(0, 10) : "")}
              placeholder="To"
            />
          </>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : sortedRuns.length === 0 ? (
        <div className="py-16 text-center text-sm text-muted-foreground">
          {currentPage > 1 && totalRuns > 0
            ? "No more runs on this page"
            : triggeredByFilter &&
                statusFilter === "all" &&
                triggerFilter === "all" &&
                typeFilter === "all" &&
                timeFilter === "all"
              ? `No CI runs triggered by ${triggeredByFilter}`
              : "No CI runs found"}
        </div>
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Run</TableHead>
                <TableHead>
                  <SortableHeader
                    label="Status"
                    field="status"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Tests"
                    field="tests"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>Triggered</TableHead>
                <TableHead>Commit</TableHead>
                <TableHead>
                  <SortableHeader
                    label="Started"
                    field="started_at"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortableHeader
                    label="Duration"
                    field="duration"
                    currentSort={sortField}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                    icon={<Timer className="size-3.5" />}
                  />
                </TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRuns.map((run) => (
                <TableRow
                  key={run.run_id}
                  className={cn(
                    "cursor-pointer",
                    run.status === "timed_out"
                      ? "border-l-2 border-l-slate-500"
                      : (run.failed > 0 || run.status === "failed") &&
                          "border-l-2 border-l-rose-500",
                  )}
                  onClick={() => navigate(`/ci/${run.run_id}`)}
                >
                  <TableCell>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm font-medium">#{run.run_id}</span>
                        <TestTypeBadge type={run.test_type ?? "criu"} />
                      </div>
                      {run.git_branch && (
                        <p className="text-xs text-muted-foreground">{run.git_branch}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <CiStatusBadge status={runDisplayStatus(run)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      {run.passed > 0 && (
                        <span className="text-emerald-500">{run.passed} passed</span>
                      )}
                      {run.failed > 0 && <span className="text-rose-500">{run.failed} failed</span>}
                      {run.xfailed > 0 && (
                        <span className="text-yellow-600">{run.xfailed} xfail</span>
                      )}
                      {run.xpassed > 0 && (
                        <span className="text-cyan-600">{run.xpassed} xpass</span>
                      )}
                      {run.skipped > 0 && (
                        <span className="text-muted-foreground">{run.skipped} skipped</span>
                      )}
                      {run.passed === 0 && run.failed === 0 && run.skipped === 0 && (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <TriggerBadge triggeredBy={run.triggered_by} />
                  </TableCell>
                  <TableCell>
                    {run.git_sha ? (
                      <span className="font-mono text-xs">{run.git_sha.slice(0, 7)}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {run.started_at ? (
                      <Tooltip>
                        <TooltipTrigger>
                          <span className="text-sm text-muted-foreground">
                            {formatRelativeTime(new Date(run.started_at))}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{formatTimestamp(run.started_at)}</TooltipContent>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {formatDurationFromRange(run.started_at, run.completed_at)}
                  </TableCell>
                  <TableCell>
                    {run.run_url && (
                      <a
                        href={run.run_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="Open CI run"
                      >
                        <ExternalLink className="size-4" />
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={goToPage}
        />
      )}
    </div>
  );
}

function PaginationControls({
  currentPage,
  totalPages,
  onPageChange,
}: {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}) {
  const pageNumbers = pageWindow(currentPage, totalPages);
  return (
    <nav className="flex items-center justify-between gap-4" aria-label="CI runs pagination">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
      >
        <ChevronLeft className="size-4" />
        Previous
      </Button>
      <div className="flex items-center gap-1">
        {pageNumbers.map((entry) =>
          entry.kind === "ellipsis" ? (
            <span key={entry.key} className="px-2 text-sm text-muted-foreground">
              …
            </span>
          ) : (
            <Button
              key={entry.key}
              variant={entry.page === currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(entry.page)}
              aria-current={entry.page === currentPage ? "page" : undefined}
              className="min-w-9"
            >
              {entry.page}
            </Button>
          ),
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
      >
        Next
        <ChevronRight className="size-4" />
      </Button>
    </nav>
  );
}

type PageEntry = { kind: "page"; page: number; key: string } | { kind: "ellipsis"; key: string };

// Compact page list: always show 1 and last, the current page and its two
// neighbours, with "…" filling the gaps. e.g. [1, …, 4, 5, 6, …, 23].
function pageWindow(current: number, total: number): PageEntry[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, index) => {
      const page = index + 1;
      return { kind: "page", page, key: `p-${page}` };
    });
  }
  const pages: PageEntry[] = [{ kind: "page", page: 1, key: "p-1" }];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  if (start > 2) pages.push({ kind: "ellipsis", key: "gap-left" });
  for (let page = start; page <= end; page += 1) {
    pages.push({ kind: "page", page, key: `p-${page}` });
  }
  if (end < total - 1) pages.push({ kind: "ellipsis", key: "gap-right" });
  pages.push({ kind: "page", page: total, key: `p-${total}` });
  return pages;
}
